import React, { useState, useRef } from 'react';
import { parseRecipientsCsv, buildRecipientCsvTemplate } from '../utils/csvParser';
import { RECIPIENT_CSV_FIELDS } from '../utils/recipientFields';

/**
 * CsvImportModal
 *
 * Responsibility: Handle CSV file selection, parsing, validation, and import
 * Single Responsibility: CSV import workflow
 *
 * This used to read three columns in a fixed order — email, name, tags — while
 * the Recipient Browser read every field by header name. Two import screens,
 * two different file formats. Both now go through parseRecipientsCsv.
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {Function} onClose - Callback to close modal
 * @param {Function} onImport - Callback with parsed recipients: (recipients) => Promise<void>
 */
export default function CsvImportModal({ isOpen, onClose, onImport }) {
    const [parsedUsers, setParsedUsers] = useState(null);
    const [rowErrors, setRowErrors] = useState([]);
    const [warnings, setWarnings] = useState([]);
    const [error, setError] = useState('');
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    const optionalColumns = RECIPIENT_CSV_FIELDS.filter((f) => !f.required).map((f) => f.column);

    const handleTemplateDownload = () => {
        const blob = new Blob([buildRecipientCsvTemplate()], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'recipient_import_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target.result;

            try {
                const { recipients, errors, warnings: parseWarnings } = parseRecipientsCsv(text);
                setRowErrors(errors);
                setWarnings(parseWarnings);
                if (recipients.length === 0) {
                    setError(
                        errors.length > 0
                            ? 'No rows could be imported. See the list below.'
                            : 'No rows found in this file.'
                    );
                    setParsedUsers(null);
                } else {
                    setParsedUsers(recipients);
                    setError('');
                }
            } catch (err) {
                setError(err?.message || 'Failed to parse CSV');
                setParsedUsers(null);
                setRowErrors([]);
                setWarnings([]);
            }
        };

        reader.onerror = () => {
            setError('Failed to read file');
            setParsedUsers(null);
        };

        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (!parsedUsers || parsedUsers.length === 0) return;

        setImporting(true);
        setError('');

        try {
            await onImport(parsedUsers);
            handleClose();
        } catch (err) {
            setError(err?.message || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    const handleClose = () => {
        setParsedUsers(null);
        setRowErrors([]);
        setWarnings([]);
        setError('');
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        onClose?.();
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) handleClose();
    };

    if (!isOpen) return null;

    return (
        <div style={styles.overlay} onClick={handleBackdropClick}>
            <div style={styles.modal} role="dialog" aria-labelledby="import-title" aria-modal="true">
                <div style={styles.header}>
                    <h2 id="import-title" style={styles.title}>Import Users from CSV</h2>
                    <button style={styles.closeButton} onClick={handleClose} aria-label="Close">×</button>
                </div>

                <div style={styles.instructions}>
                    <p style={styles.instructionText}>
                        One column is required: <strong>email</strong>. Everything else is optional.
                        Columns are matched by name, so the order does not matter, and you can leave
                        any optional column out or leave its cells blank.
                    </p>
                    <p style={styles.exampleText}>
                        Optional columns: {optionalColumns.join(', ')}
                    </p>
                    <button onClick={handleTemplateDownload} style={styles.templateButton}>
                        Download blank template
                    </button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                {rowErrors.length > 0 && (
                    <div style={styles.error}>
                        <strong>{rowErrors.length} row{rowErrors.length !== 1 ? 's' : ''} will be skipped:</strong>
                        <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                            {rowErrors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                            {rowErrors.length > 10 && <li>…and {rowErrors.length - 10} more.</li>}
                        </ul>
                    </div>
                )}

                {warnings.length > 0 && (
                    <div style={styles.warning}>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {warnings.slice(0, 10).map((w, i) => <li key={i}>{w}</li>)}
                            {warnings.length > 10 && <li>…and {warnings.length - 10} more.</li>}
                        </ul>
                    </div>
                )}

                <div style={styles.fileSection}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleFileSelect}
                        style={styles.fileInput}
                        disabled={importing}
                    />
                </div>

                {parsedUsers && parsedUsers.length > 0 && (
                    <div style={styles.preview}>
                        <h3 style={styles.previewTitle}>Preview ({parsedUsers.length} users)</h3>
                        <div style={styles.previewList}>
                            {parsedUsers.slice(0, 5).map((user, idx) => (
                                <div key={idx} style={styles.previewItem}>
                                    <div><strong>{user.email}</strong></div>
                                    <div style={styles.previewDetails}>
                                        {user.name && <span>{user.name}</span>}
                                        {user.tags && user.tags.length > 0 && (
                                            <span style={styles.tags}>
                                                Tags: {user.tags.join(', ')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {parsedUsers.length > 5 && (
                                <div style={styles.moreUsers}>
                                    + {parsedUsers.length - 5} more users
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div style={styles.actions}>
                    <button style={styles.cancelButton} onClick={handleClose} disabled={importing}>
                        Cancel
                    </button>
                    <button
                        style={{ ...styles.importButton, ...(importing && styles.buttonDisabled) }}
                        onClick={handleImport}
                        disabled={!parsedUsers || parsedUsers.length === 0 || importing}
                    >
                        {importing ? 'Importing...' : `Import ${parsedUsers?.length || 0} Users`}
                    </button>
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: 8,
        padding: 24,
        width: '100%',
        maxWidth: 600,
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
    },
    title: {
        margin: 0,
        fontSize: 20,
        fontWeight: 600,
    },
    closeButton: {
        background: 'transparent',
        border: 'none',
        fontSize: 28,
        cursor: 'pointer',
        padding: 4,
        lineHeight: 1,
        color: '#5F6368',
    },
    instructions: {
        backgroundColor: 'var(--color-bg-base)',
        padding: 16,
        borderRadius: 4,
        marginBottom: 16,
    },
    instructionText: {
        margin: '0 0 8px 0',
        fontSize: 14,
        color: '#3C4043',
    },
    exampleText: {
        margin: 0,
        fontSize: 13,
        color: '#5F6368',
    },
    error: {
        backgroundColor: '#FFEBEE',
        color: '#C62828',
        padding: 12,
        borderRadius: 4,
        marginBottom: 16,
        fontSize: 14,
    },
    warning: {
        backgroundColor: '#FFF8E1',
        color: '#8D6E00',
        padding: 12,
        borderRadius: 4,
        marginBottom: 16,
        fontSize: 13,
    },
    templateButton: {
        marginTop: 10,
        padding: '6px 12px',
        backgroundColor: '#fff',
        color: '#3C4043',
        border: '1px solid #DADCE0',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 13,
    },
    fileSection: {
        marginBottom: 16,
    },
    fileInput: {
        width: '100%',
        padding: 8,
        fontSize: 14,
    },
    preview: {
        marginBottom: 16,
    },
    previewTitle: {
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 12,
    },
    previewList: {
        border: '1px solid #E8EAED',
        borderRadius: 4,
        maxHeight: 300,
        overflow: 'auto',
    },
    previewItem: {
        padding: 12,
        borderBottom: '1px solid #E8EAED',
    },
    previewDetails: {
        display: 'flex',
        gap: 12,
        marginTop: 4,
        fontSize: 13,
        color: '#5F6368',
    },
    tags: {
        fontStyle: 'italic',
    },
    moreUsers: {
        padding: 12,
        textAlign: 'center',
        color: '#5F6368',
        fontSize: 13,
        fontStyle: 'italic',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
        marginTop: 16,
    },
    cancelButton: {
        padding: '8px 16px',
        backgroundColor: '#fff',
        color: '#3C4043',
        border: '1px solid #DADCE0',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
    },
    importButton: {
        padding: '8px 16px',
        backgroundColor: '#5B9FFF',
        color: '#fff',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
    },
    buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
};
